import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import TemplateSelector from 'components/field/TemplateSelector';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import * as F from 'components/form/FormLayout.styled';
import Alert from 'components/global/Alert';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import CustomEmailModal from 'pages/Settings/Email/EmailSettings/CustomEmailModal';

import fetchLanguageResource from 'services/common/fetchLanguageResource';
import { AUTOCOMPLETE, EMAIL, ENUMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import EmailAccountForm from '../EmailAccounts/EmailAccountForm';

const EmailSettingForm = NiceModal.create(({ id, mode, modalName }) => {
	const isAuthorized = useAccessControl();

	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					emailType: yup.string().nullable().required(),
					emailAccountID: yup.number().nullable().required(),
					brandID: yup.number().nullable(),
					language: yup.string().nullable().required(),
					status: yup.string().nullable().required(),
					templateName: yup.string().nullable().required(),
					subject: yup.string().nullable().required(),
					replyTo: yup.string().email().nullable().required()
				})
				.required(),
		[]
	);

	const viewMode = mode === 'view';

	const { closeModal } = useModalRouter();
	const modal = useModal();

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const { showError, showSuccess } = useAlert();
	const { t } = useTranslation();

	const dispatch = useDispatch();
	const axiosPrivate = useAxiosPrivate();
	const { LanguageCode } = useLocale();
	const isEnterpriseUser = useSelector((state) => state.user.planName) === 'EnterprisePackage';

	const fillForm = useCallback(async () => {
		const defaultValues = {
			emailType: null,
			emailAccountID: null,
			brandID: null,
			language: LanguageCode,
			status: 'active',
			templateName: 'template1',
			content: null,
			subject: null,
			replyTo: null
		};
		if (!id) {
			return defaultValues;
		}

		try {
			const response = await axiosPrivate.post(EMAIL.GET_EMAIL_SETTING, {
				ID: id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				...defaultValues,
				...data
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		setValue,
		watch,
		getValues,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const onSubmit = async (data) => {
		const URL = id ? EMAIL.UPDATE_EMAIL_SETTING : EMAIL.ADD_EMAIL_SETTING;

		await axiosPrivate
			.post(URL, {
				ID: id,
				emailType: data.emailType,
				emailAccountID: data.emailAccountID,
				brandID: data.brandID ? data.brandID : undefined,
				language: data.language,
				templateName: data.templateName,
				content: data.content,
				subject: data.subject,
				replyTo: data.replyTo,
				status: data.status === 'active'
			})
			.then((response) => {
				const { status } = response;

				if (status !== 200) {
					showError();
					return;
				}

				showSuccess();
				dispatch(updateDynamicListData(DYNAMICLISTS.EMAIL_SETTINGS));
				handleCloseModal();
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.F010') {
					showError({
						header: 'errorMsg.emailSettingAlreadyExists',
						message: 'error.F010'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const defaultColSpan = {
		md: 6
	};

	const largeDefaultColSpan = {
		md: 12
	};

	const brandAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const brandAutocompleteLabels = useMemo(() => ['Name'], []);

	const emailAccountsAutocompleteColumns = useMemo(() => [{ accessor: 'Email', width: 200 }], []);
	const emailAccountsAutocompleteLabels = useMemo(() => ['Email'], []);

	const emailTypes = useMemo(
		() => ({
			orderTracking: [
				{
					content: 'email.orderTracking.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/OrderTracking.png',
					name: 'template1'
				}
			],
			addressVerification: [
				{
					content: 'email.addressVerification.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/DeliveryConfirmation.png',
					name: 'template1'
				}
			],
			failedDeliveryAttempt: [
				{
					content: 'email.failedDeliveryAttempt.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/FailedDeliveryAttempt.png',
					name: 'template1'
				}
			],
			orderFeedback: [
				{
					content: 'email.orderFeedback.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/OrderFeedback.png',
					name: 'template1'
				}
			],
			orderReturnRequest: [
				{
					content: 'email.orderReturnRequest.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/OrderReturnRequest.png',
					name: 'template1'
				}
			],
			orderNotification: [
				{
					content: 'email.orderNotification.template1',
					image: 'https://storage.googleapis.com/tryoto-public/mail-templates/OrderNotification.png',
					name: 'template1'
				}
			]
		}),
		[]
	);

	const handleEmailTypeChange = (value) => {
		setValue(`emailType`, value, { shouldValidate: true, shouldDirty: true });
		setValue('templateName', 'template1', {
			shouldDirty: true,
			shouldValidate: true
		});
		const content = emailTypes[value]?.find((x) => x.name === 'template1').content;
		setValue(
			'content',
			t(content, {
				lng: watch('language')
			})
		);
	};

	const handleLanguageChange = (value) => {
		setValue(`language`, value, { shouldValidate: true, shouldDirty: true });

		fetchLanguageResource(value).then(() => {
			const content = emailTypes[watch('emailType')]?.find((x) => x.name === 'template1').content;
			setValue(
				'content',
				t(content, {
					lng: value
				})
			);
		});
	};

	const specialKeys = useMemo(
		() => [
			{ text: t('link'), value: '{{link}}' },
			{ text: t('customerName'), value: '{{customerName}}' },
			{ text: t('orderID'), value: '{{orderId}}' },
			{ text: t('deliveryCompany'), value: '{{dcName}}' },
			{ text: t('customerAddress'), value: '{{customerAddress}}' },
			{ text: t('storeName'), value: '{{storeName}}' },
			{ text: t('companyLogo'), value: '{{companyLogo}}' }
		],
		[]
	);

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-emailAccounts"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={viewMode ? 'viewEmailSetting' : mode === 'edit' ? 'editEmailSetting' : 'addEmailSetting'}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">
					{viewMode ? 'view' : mode === 'edit' ? 'editEmailSetting' : 'addEmailSettings'}
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="emailType" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							enumName={
								isAuthorized('EnterprisePackage')
									? ENUMS.EMAIL_TEMP_TYPES_V2_ENTERPRISE
									: ENUMS.EMAIL_TEMP_TYPES_V2
							}
							name="emailType"
							value={watch('emailType')}
							setValue={handleEmailTypeChange}
							disabled={viewMode}
							searchable={false}
							valueRemovable={false}
						/>
					</FormItem>

					<FormItem label="emailAccounts" colSpan={defaultColSpan} isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.EMAIL_ACCOUNTS}
							columns={emailAccountsAutocompleteColumns}
							selectionLabelAccessor={emailAccountsAutocompleteLabels}
							value={watch('emailAccountID')}
							name={`emailAccountID`}
							firstItem={{
								action: () =>
									NiceModal.show(EmailAccountForm, {
										setEmailAccount: (value) => {
											setValue(`emailAccountID`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}
									}),
								label: <Typography color="var(--blue)">addANewEmailAccount</Typography>
							}}
							setValue={(value) => {
								setValue('emailAccountID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							disabled={viewMode}
						/>
					</FormItem>

					<FormItem label="subject" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="subject" disabled={viewMode} />
					</FormItem>

					<FormItem label="replyTo" colSpan={defaultColSpan} isLoading={isLoading} required>
						<StringInput name="replyTo" disabled={viewMode} />
					</FormItem>

					<FormItem label="brand" colSpan={defaultColSpan} isLoading={isLoading} roleName="BrandingModule">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.BRANDS}
							columns={brandAutocompleteColumns}
							selectionLabelAccessor={brandAutocompleteLabels}
							value={watch('brandID')}
							setValue={(value) => {
								setValue('brandID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
						/>
					</FormItem>

					<FormItem label="language" colSpan={defaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							valueRemovable={false}
							enumName={ENUMS.LANGUAGE_CODES}
							value={watch(`language`)}
							setValue={handleLanguageChange}
							disabled={viewMode}
						/>
					</FormItem>

					<FormItem label="status" colSpan={largeDefaultColSpan} isLoading={isLoading} required>
						<EnumSelect
							searchable={false}
							enumName={ENUMS.ACTIVE_INACTIVE}
							name="status"
							valueRemovable={false}
							value={watch(`status`)}
							setValue={(value) => {
								setValue(`status`, value, { shouldValidate: true, shouldDirty: true });
							}}
							disabled={viewMode}
						/>
					</FormItem>
					<FormItem colSpan={largeDefaultColSpan}>
						<Alert content="smsFormSettingsInfoText" showIcon />
					</FormItem>
					{watch('emailType') && (
						<FormItem label="emailTemplates" toolTip="addEmailSetting.tooltip" required>
							<F.TemplateSelectorContainer>
								{emailTypes[watch('emailType')]?.map((template, index) => (
									<TemplateSelector
										key={index}
										name={template.name}
										imageUrl={template.image}
										className={watch('templateName') === template.name ? 'selected' : ''}
										checkAction={
											viewMode
												? undefined
												: () => {
														setValue('templateName', template.name, {
															shouldDirty: true,
															shouldValidate: true
														});
													}
										}
									/>
								))}
								<TemplateSelector
									name="custom"
									imageUrl="https://storage.googleapis.com/tryoto-public/mail-templates/OrderTracking.png"
									className={watch('templateName') === 'custom' ? 'selected' : ''}
									checkAction={
										viewMode
											? undefined
											: () => {
													setValue('templateName', 'custom', {
														shouldDirty: true,
														shouldValidate: true
													});
												}
									}
									configAction={() => {
										NiceModal.show(CustomEmailModal, {
											value: getValues('content'),
											setValue: (value) => {
												setValue('content', value, { shouldValidate: true, shouldDirty: true });
											},
											initProps: {
												setup: (editor) => {
													const onAction = (autocompleteApi, rng, value) => {
														editor.selection.setRng(rng);
														editor.insertContent(value);
														autocompleteApi.hide();
													};

													const getMatchedChars = (pattern) =>
														specialKeys.filter(
															(char) => char.value.indexOf(pattern) !== -1
														);

													editor.ui.registry.addAutocompleter('specialKeys', {
														trigger: '{{',
														minChars: 0,
														columns: 1,
														onAction,
														fetch: (pattern) =>
															new Promise((resolve) => {
																const results = getMatchedChars(pattern).map(
																	(char) => ({
																		type: 'autocompleteitem',
																		value: char.value,
																		text: char.text
																	})
																);
																resolve(results);
															})
													});

													editor.ui.registry.addAutocompleter('specialKeysSplash', {
														trigger: '/',
														minChars: 0,
														columns: 1,
														onAction,
														fetch: (pattern) =>
															new Promise((resolve) => {
																const results = getMatchedChars(pattern).map(
																	(char) => ({
																		type: 'autocompleteitem',
																		value: char.value,
																		text: char.text
																	})
																);
																resolve(results);
															})
													});
												}
											}
										});
									}}
									isConfigurable={!viewMode}
									disabled={!isEnterpriseUser}
									disabledDescription="customEmailLockedDescription"
								/>
							</F.TemplateSelectorContainer>
						</FormItem>
					)}
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					<Button
						label="save"
						disabled={!isDirty}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						iconPos="right"
					/>
				</ModalActions>
			)}
		</Modal>
	);
});

export default EmailSettingForm;
